
import { Popover, Typography, Box, Link, Divider, IconButton, Button, Stack } from '@mui/material';
import { OpenInNew, AutoAwesome, Close, BiotechOutlined } from '@mui/icons-material';

interface CitationPopupProps {
    citation: any; // Using any for flexibility with RAG result structure
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onNavigateToDrug?: (drugId: string) => void;
    onSearchByMolecule?: (molecule: string) => void;
}

export function CitationPopup({ citation, anchorEl, onClose, onNavigateToDrug, onSearchByMolecule }: CitationPopupProps) {
    const open = Boolean(anchorEl);

    if (!citation) return null;

    // Extract relevant fields from RAG result structure
    const source = citation.metadata?.title || citation.norm_source || citation.source || 'Unknown Source';
    const rawContent = citation.content || citation.chunk_content || '';
    const url = citation.metadata?.source || citation.source;
    const section = citation.metadata?.section_path || '';
    const type = citation.metadata?.type || 'document';
    const id = citation.metadata?.id || '';
    const activeSubstances: string[] = citation.metadata?.active_substances || [];

    // Filter content to show only text after --- separator (metadata is before)
    const filterContent = (text: string): string => {
        const separatorIdx = text.indexOf('---');
        if (separatorIdx !== -1) {
            return text.substring(separatorIdx + 3).trim();
        }
        return text;
    };
    const content = filterContent(rawContent);

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'center',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: 'center',
            }}
            anchorReference="anchorPosition"
            anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
            PaperProps={{
                sx: {
                    maxWidth: 500,
                    p: 2,
                    boxShadow: 6,
                    borderRadius: 2
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesome fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight="bold">
                        {type === 'medication' ? 'Médicament' : 'Citation'}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ mt: -0.5, mr: -0.5 }}>
                    <Close fontSize="small" />
                </IconButton>
            </Box>

            <Typography variant="body2" component="div" sx={{
                mb: 2,
                maxHeight: 400,
                overflowY: 'auto',
                bgcolor: 'grey.50',
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                '& p': {
                    margin: '0.5em 0',
                },
                '& ul, & ol': {
                    paddingLeft: '1.5em',
                    margin: '0.5em 0',
                },
            }}
            dangerouslySetInnerHTML={{ __html: content }}
            />

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    SOURCE
                </Typography>
                <Typography variant="body2" fontWeight="medium" color="primary.main">
                    {source}
                </Typography>

                {section && (
                    <Typography variant="caption" color="text.secondary">
                        Section: {section}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                    {type === 'medication' ? (
                        <Stack direction="row" spacing={1}>
                            {id && onNavigateToDrug && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AutoAwesome />}
                                    onClick={() => {
                                        onNavigateToDrug(id);
                                        onClose();
                                    }}
                                    sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                                >
                                    Voir fiche
                                </Button>
                            )}
                            {activeSubstances.length > 0 && onSearchByMolecule && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<BiotechOutlined />}
                                    onClick={() => {
                                        onSearchByMolecule(activeSubstances[0]);
                                        onClose();
                                    }}
                                    sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                                >
                                    Autres formes ({activeSubstances[0]})
                                </Button>
                            )}
                        </Stack>
                    ) : (
                        url && (url.startsWith('http') || url.startsWith('file')) && (
                            <Link
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}
                            >
                                Voir document <OpenInNew sx={{ fontSize: 12 }} />
                            </Link>
                        )
                    )}
                </Box>
            </Box>
        </Popover>
    );
}
